import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Collapse,
  Alert,
  Snackbar,
  Tooltip,
  Menu as MuiMenu,
  Badge,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  DeleteSweep as ClearIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ChevronRight as ExpandIcon,
  ExpandMore as CollapseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Speed as SpeedIcon,
  Circle as CircleIcon,
  Block as BlockIcon,
  CheckCircle as AllowIcon,
  FileDownload as ExportIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import {
  setConnected,
  setCapturing,
  addRequest,
  clearRequests,
  setSelectedRequests,
  addFilter,
  removeFilter,
  updateFilter,
} from '../redux/slices/networkMonitorSlice';
import { createRuleSet, createRule } from '../redux/slices/networkRulesSlice';
import networkMonitorService from '../services/api/networkMonitorService';

const REQUEST_TYPES = ['All', 'XHR', 'JS', 'CSS', 'Img', 'Media', 'Font', 'Doc', 'WS', 'Other'];

const TYPE_MAP = {
  xhr: 'XHR',
  xmlhttprequest: 'XHR',
  fetch: 'XHR',
  script: 'JS',
  stylesheet: 'CSS',
  image: 'Img',
  media: 'Media',
  font: 'Font',
  document: 'Doc',
  websocket: 'WS',
};

function getRequestType(request) {
  const type = (request.type || request.resourceType || '').toLowerCase();
  return TYPE_MAP[type] || 'Other';
}

function getStatusColor(status) {
  if (!status) return 'grey.400';
  if (status >= 200 && status < 300) return 'success.main';
  if (status >= 300 && status < 400) return 'warning.main';
  if (status >= 400) return 'error.main';
  return 'grey.400';
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(ms) {
  if (!ms && ms !== 0) return '-';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

function truncateUrl(url, maxLen = 80) {
  if (!url) return '';
  try {
    const u = new URL(url);
    const path = u.pathname + u.search;
    if (path.length > maxLen) return path.substring(0, maxLen) + '...';
    return path;
  } catch {
    if (url.length > maxLen) return url.substring(0, maxLen) + '...';
    return url;
  }
}

function getDomain(url) {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

function getTotalSize(requests) {
  const total = requests.reduce((sum, r) => sum + (r.size || 0), 0);
  return formatSize(total);
}

// -- Demo data generator for when WebSocket is not available --
let demoId = 0;
const DEMO_URLS = [
  'https://api.example.com/v1/users',
  'https://cdn.example.com/assets/bundle.js',
  'https://cdn.example.com/styles/main.css',
  'https://cdn.example.com/images/logo.png',
  'https://analytics.tracker.com/collect',
  'https://ads.network.com/pixel.gif',
  'https://api.example.com/v1/automations',
  'https://fonts.googleapis.com/css2?family=Roboto',
  'https://api.example.com/v1/health',
  'https://cdn.example.com/media/video.mp4',
];
const DEMO_TYPES = ['xmlhttprequest', 'script', 'stylesheet', 'image', 'xmlhttprequest', 'image', 'xmlhttprequest', 'font', 'xmlhttprequest', 'media'];
const DEMO_METHODS = ['GET', 'GET', 'GET', 'GET', 'POST', 'GET', 'GET', 'GET', 'GET', 'GET'];
const DEMO_STATUSES = [200, 200, 200, 200, 204, 200, 401, 200, 200, 206];

function generateDemoRequest(filters) {
  const idx = demoId % DEMO_URLS.length;
  const url = DEMO_URLS[idx];
  const blocked = filters.some((f) => {
    try {
      const re = new RegExp(f.pattern);
      return f.type === 'block' && re.test(url);
    } catch {
      return url.includes(f.pattern);
    }
  });
  const req = {
    id: `demo-${++demoId}`,
    url,
    method: DEMO_METHODS[idx],
    status: blocked ? null : DEMO_STATUSES[idx],
    type: DEMO_TYPES[idx],
    size: blocked ? 0 : Math.floor(Math.random() * 500000),
    duration: blocked ? 0 : Math.floor(Math.random() * 2000),
    blocked,
    timestamp: Date.now(),
    headers: {
      'content-type': idx < 5 ? 'application/json' : 'text/html',
      'cache-control': 'no-cache',
    },
  };
  return req;
}

// -- Filter Rule Dialog --
function RuleDialog({ open, onClose, onSave, initialRule }) {
  const [pattern, setPattern] = useState('');
  const [type, setType] = useState('block');
  const [regexError, setRegexError] = useState('');

  useEffect(() => {
    if (initialRule) {
      setPattern(initialRule.pattern || '');
      setType(initialRule.type || 'block');
    } else {
      setPattern('');
      setType('block');
    }
    setRegexError('');
  }, [initialRule, open]);

  const handlePatternChange = (e) => {
    const val = e.target.value;
    setPattern(val);
    try {
      if (val) new RegExp(val);
      setRegexError('');
    } catch (err) {
      setRegexError(err.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialRule ? 'Edit Rule' : 'Create Rule'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="URL Pattern (regex)"
          fullWidth
          value={pattern}
          onChange={handlePatternChange}
          error={!!regexError}
          helperText={regexError || 'Regular expression to match request URLs'}
          sx={{ mt: 1 }}
        />
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Rule Type</InputLabel>
          <Select value={type} onChange={(e) => setType(e.target.value)} label="Rule Type">
            <MenuItem value="block">Block</MenuItem>
            <MenuItem value="allow">Allow</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => onSave({ pattern, type })}
          variant="contained"
          disabled={!pattern || !!regexError}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -- Request Detail Panel --
function RequestDetail({ request }) {
  if (!request) return null;
  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" gutterBottom>Full URL</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 2, fontFamily: 'monospace', fontSize: '0.8rem' }}>
        {request.url}
      </Typography>
      {request.headers && (
        <>
          <Typography variant="subtitle2" gutterBottom>Headers</Typography>
          <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 2 }}>
            {Object.entries(request.headers).map(([k, v]) => (
              <Box key={k}><strong>{k}:</strong> {v}</Box>
            ))}
          </Box>
        </>
      )}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Typography variant="body2"><strong>Size:</strong> {formatSize(request.size)}</Typography>
        <Typography variant="body2"><strong>Duration:</strong> {formatDuration(request.duration)}</Typography>
        <Typography variant="body2"><strong>Type:</strong> {request.type || '-'}</Typography>
      </Box>
    </Box>
  );
}

// -- Main Component --
export default function NetworkMonitorPage() {
  const dispatch = useDispatch();
  const { requests, filters, selectedRequests, capturing, connected, filterStats } = useSelector(
    (state) => state.networkMonitor
  );

  const [searchText, setSearchText] = useState('');
  const [activeType, setActiveType] = useState('All');
  const [expandedRow, setExpandedRow] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextRequest, setContextRequest] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [ruleSetName, setRuleSetName] = useState('');

  const demoInterval = useRef(null);
  const tableEndRef = useRef(null);

  const [extensionMode, setExtensionMode] = useState(false);
  const [blockerEnabled, setBlockerEnabled] = useState(false);
  const [blockerRuleCount, setBlockerRuleCount] = useState(0);

  // Connect to extension bridge or WebSocket on mount
  useEffect(() => {
    let mounted = true;
    networkMonitorService
      .connect()
      .then(() => {
        if (mounted) {
          dispatch(setConnected(true));
          setExtensionMode(networkMonitorService.mode === 'extension');
          // Fetch initial content blocker status
          networkMonitorService.getBlockerStatus().then((status) => {
            if (mounted) {
              setBlockerEnabled(!!status.enabled);
              setBlockerRuleCount(status.ruleCount || 0);
            }
          });
        }
      })
      .catch(() => {
        if (mounted) dispatch(setConnected(false));
      });

    return () => {
      mounted = false;
      networkMonitorService.disconnect();
      dispatch(setConnected(false));
    };
  }, [dispatch]);

  const handleToggleBlocker = useCallback(async () => {
    const result = await networkMonitorService.toggleContentBlocker();
    setBlockerEnabled(!!result.enabled);
    if (result.ruleCount !== undefined) setBlockerRuleCount(result.ruleCount);
    showSnackbar(result.enabled ? 'Content blocker enabled' : 'Content blocker disabled');
  }, []);

  const handleReloadFilters = useCallback(async () => {
    const result = await networkMonitorService.reloadFilters();
    if (result.ruleCount !== undefined) setBlockerRuleCount(result.ruleCount);
    if (result.enabled !== undefined) setBlockerEnabled(!!result.enabled);
    showSnackbar(`Filters reloaded (${result.ruleCount || 0} DNR rules)`);
  }, []);

  // Listen for new requests from WebSocket
  useEffect(() => {
    const handleNewRequest = (data) => {
      dispatch(addRequest(data));
    };
    networkMonitorService.onNewRequest(handleNewRequest);
    return () => {
      networkMonitorService.offNewRequest(handleNewRequest);
    };
  }, [dispatch]);

  // Toggle capture (extension bridge, WebSocket, or demo fallback)
  const handleToggleCapture = useCallback(() => {
    if (capturing) {
      dispatch(setCapturing(false));
      networkMonitorService.stopCapture();
      if (demoInterval.current) {
        clearInterval(demoInterval.current);
        demoInterval.current = null;
      }
    } else {
      dispatch(setCapturing(true));
      dispatch(clearRequests());
      networkMonitorService.startCapture();
      if (!connected) {
        // Demo mode: generate sample requests when no extension and no WS
        demoInterval.current = setInterval(() => {
          dispatch(addRequest(generateDemoRequest(filters)));
        }, 800);
      }
    }
  }, [capturing, connected, dispatch, filters]);

  // Cleanup demo interval
  useEffect(() => {
    return () => {
      if (demoInterval.current) clearInterval(demoInterval.current);
    };
  }, []);

  // Filter requests
  const filteredRequests = useMemo(() => {
    let result = requests;
    if (activeType !== 'All') {
      result = result.filter((r) => getRequestType(r) === activeType);
    }
    if (searchText) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (r) =>
          (r.url || '').toLowerCase().includes(lower) ||
          (r.method || '').toLowerCase().includes(lower) ||
          String(r.status || '').includes(lower)
      );
    }
    return result;
  }, [requests, activeType, searchText]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (capturing && tableEndRef.current) {
      tableEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filteredRequests.length, capturing]);

  // Context menu handlers
  const handleContextMenu = (event, request) => {
    event.preventDefault();
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
    setContextRequest(request);
  };

  const handleCloseContext = () => {
    setContextMenu(null);
    setContextRequest(null);
  };

  const handleBlockUrl = () => {
    if (contextRequest) {
      const escaped = contextRequest.url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      dispatch(addFilter({ pattern: escaped, type: 'block', matchCount: 0 }));
      showSnackbar('Block rule created for URL');
    }
    handleCloseContext();
  };

  const handleBlockDomain = () => {
    if (contextRequest) {
      const domain = getDomain(contextRequest.url).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      dispatch(addFilter({ pattern: domain, type: 'block', matchCount: 0 }));
      showSnackbar('Block rule created for domain');
    }
    handleCloseContext();
  };

  const handleAllowUrl = () => {
    if (contextRequest) {
      const escaped = contextRequest.url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      dispatch(addFilter({ pattern: escaped, type: 'allow', matchCount: 0 }));
      showSnackbar('Allow rule created for URL');
    }
    handleCloseContext();
  };

  const handleCreateRegexRule = () => {
    if (contextRequest) {
      try {
        const u = new URL(contextRequest.url);
        const pattern = u.hostname.replace(/\./g, '\\.') + u.pathname.replace(/[0-9a-f-]{8,}/gi, '[^/]+');
        dispatch(addFilter({ pattern, type: 'block', matchCount: 0 }));
        showSnackbar('Regex rule created');
      } catch {
        showSnackbar('Could not create regex rule', 'error');
      }
    }
    handleCloseContext();
  };

  // Selection
  const handleSelectRequest = (id) => {
    const current = [...selectedRequests];
    const idx = current.indexOf(id);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(id);
    }
    dispatch(setSelectedRequests(current));
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      dispatch(setSelectedRequests(filteredRequests.map((r) => r.id)));
    } else {
      dispatch(setSelectedRequests([]));
    }
  };

  // Rule management
  const handleSaveRule = (rule) => {
    if (editingRuleIndex !== null) {
      dispatch(updateFilter({ index: editingRuleIndex, filter: { ...rule, matchCount: 0 } }));
    } else {
      dispatch(addFilter({ ...rule, matchCount: 0 }));
    }
    setRuleDialogOpen(false);
    setEditingRuleIndex(null);
    showSnackbar(editingRuleIndex !== null ? 'Rule updated' : 'Rule created');
  };

  const handleDeleteRule = (index) => {
    dispatch(removeFilter(index));
    showSnackbar('Rule deleted');
  };

  const handleEditRule = (index) => {
    setEditingRuleIndex(index);
    setRuleDialogOpen(true);
  };

  // Create rules from selection
  const handleCreateRulesFromSelection = () => {
    const selected = requests.filter((r) => selectedRequests.includes(r.id));
    const domains = [...new Set(selected.map((r) => getDomain(r.url)))];
    domains.forEach((domain) => {
      const escaped = domain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      dispatch(addFilter({ pattern: escaped, type: 'block', matchCount: 0 }));
    });
    showSnackbar(`Created ${domains.length} rule(s) from selection`);
    dispatch(setSelectedRequests([]));
  };

  // Export as network rule set
  const handleExportRuleSet = async () => {
    if (!ruleSetName.trim() || filters.length === 0) return;
    try {
      const result = await dispatch(
        createRuleSet({ name: ruleSetName.trim(), description: 'Created from Network Monitor' })
      ).unwrap();
      for (const f of filters) {
        await dispatch(
          createRule({
            ruleSetId: result.id,
            pattern: f.pattern,
            action: f.type,
            resourceTypes: [],
            isActive: true,
          })
        ).unwrap();
      }
      showSnackbar(`Exported ${filters.length} rules to "${ruleSetName}"`);
      setExportDialogOpen(false);
      setRuleSetName('');
    } catch (err) {
      showSnackbar('Failed to export rules: ' + (err.message || 'Unknown error'), 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Match counts for filter rules
  const filtersWithCounts = useMemo(() => {
    return filters.map((f) => {
      let count = 0;
      try {
        const re = new RegExp(f.pattern);
        count = requests.filter((r) => re.test(r.url)).length;
      } catch {
        count = requests.filter((r) => (r.url || '').includes(f.pattern)).length;
      }
      return { ...f, matchCount: count };
    });
  }, [filters, requests]);

  const blockedCount = filterStats.blocked;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)' }}>
      {/* Top Toolbar */}
      <Paper sx={{ mb: 1 }}>
        <Toolbar variant="dense" sx={{ gap: 1, flexWrap: 'wrap', py: 1 }}>
          <Button
            variant="contained"
            color={capturing ? 'error' : 'success'}
            startIcon={capturing ? <StopIcon /> : <PlayIcon />}
            onClick={handleToggleCapture}
            size="small"
          >
            {capturing ? 'Stop Capture' : 'Start Capture'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={() => {
              dispatch(clearRequests());
              if (extensionMode) {
                networkMonitorService.clearCapture();
              }
            }}
            size="small"
          >
            Clear
          </Button>
          <TextField
            size="small"
            placeholder="Filter by URL, method, status..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {REQUEST_TYPES.map((type) => (
              <Chip
                key={type}
                label={type}
                size="small"
                variant={activeType === type ? 'filled' : 'outlined'}
                color={activeType === type ? 'primary' : 'default'}
                onClick={() => setActiveType(type)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {filteredRequests.length} requests | {getTotalSize(filteredRequests)} | {blockedCount} blocked
          </Typography>
          <Tooltip title="Filter Rules">
            <IconButton size="small" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <Badge badgeContent={filters.length} color="primary">
                <FilterIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </Paper>

      {/* Main content area */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Request Table */}
        <TableContainer component={Paper} sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={
                      filteredRequests.length > 0 &&
                      selectedRequests.length === filteredRequests.length
                    }
                    indeterminate={
                      selectedRequests.length > 0 &&
                      selectedRequests.length < filteredRequests.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ width: 40 }}>Status</TableCell>
                <TableCell sx={{ width: 70 }}>Method</TableCell>
                <TableCell>URL</TableCell>
                <TableCell sx={{ width: 60 }}>Type</TableCell>
                <TableCell sx={{ width: 80 }} align="right">Size</TableCell>
                <TableCell sx={{ width: 80 }} align="right">Duration</TableCell>
                <TableCell sx={{ width: 60 }} align="center">Blocked</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((req) => {
                const isExpanded = expandedRow === req.id;
                const rowColor = req.blocked
                  ? 'action.disabledBackground'
                  : req.status >= 400
                  ? 'error.light'
                  : req.status >= 300
                  ? 'warning.light'
                  : 'inherit';
                return (
                  <React.Fragment key={req.id}>
                    <TableRow
                      hover
                      selected={selectedRequests.includes(req.id)}
                      onClick={() => setExpandedRow(isExpanded ? null : req.id)}
                      onContextMenu={(e) => handleContextMenu(e, req)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: rowColor,
                        opacity: req.blocked ? 0.6 : 1,
                        '&:hover': { opacity: 1 },
                      }}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selectedRequests.includes(req.id)}
                          onChange={() => handleSelectRequest(req.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <CircleIcon
                          sx={{ fontSize: 12, color: getStatusColor(req.status) }}
                        />
                        <Typography variant="caption" sx={{ ml: 0.5 }}>
                          {req.status || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={req.method || 'GET'}
                          size="small"
                          variant="outlined"
                          color={req.method === 'POST' ? 'warning' : req.method === 'DELETE' ? 'error' : 'default'}
                          sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={req.url} placement="bottom-start">
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 500,
                            }}
                          >
                            {truncateUrl(req.url)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {getRequestType(req)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">{formatSize(req.size)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">{formatDuration(req.duration)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {req.blocked && (
                          <BlockIcon sx={{ fontSize: 16, color: 'error.main' }} />
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: 0 }}>
                          <RequestDetail request={req} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              <tr ref={tableEndRef} />
            </TableBody>
          </Table>
          {filteredRequests.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <SpeedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No requests captured
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Click "Start Capture" to begin monitoring network requests
              </Typography>
            </Box>
          )}
        </TableContainer>

        {/* Right Sidebar - Filter Rules */}
        <Collapse orientation="horizontal" in={sidebarOpen}>
          <Paper
            sx={{
              width: 320,
              height: '100%',
              overflow: 'auto',
              borderLeft: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Filter Rules
                </Typography>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    setEditingRuleIndex(null);
                    setRuleDialogOpen(true);
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>

              {filters.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No filter rules defined. Right-click a request or click + to add rules.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {filtersWithCounts.map((filter, index) => (
                    <React.Fragment key={index}>
                      <ListItem sx={{ px: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          {filter.type === 'block' ? (
                            <BlockIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          ) : (
                            <AllowIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          )}
                        </Box>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                wordBreak: 'break-all',
                              }}
                            >
                              {filter.pattern}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {filter.type} | {filter.matchCount} matched
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton size="small" onClick={() => handleEditRule(index)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteRule(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < filters.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}

              <Divider sx={{ my: 2 }} />

              {selectedRequests.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={handleCreateRulesFromSelection}
                  sx={{ mb: 1 }}
                >
                  Create Rule from Selection ({selectedRequests.length})
                </Button>
              )}

              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<ExportIcon />}
                onClick={() => setExportDialogOpen(true)}
                disabled={filters.length === 0}
              >
                Export as Network Rule Set
              </Button>
            </Box>
          </Paper>
        </Collapse>
      </Box>

      {/* Extension install banner */}
      {!connected && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Install the Automatrix Chrome Extension to capture real network requests.
          Without it, the monitor runs in demo mode with simulated data.
        </Alert>
      )}

      {/* Bottom Status Bar */}
      <Paper sx={{ mt: 1, px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CircleIcon
            sx={{ fontSize: 8, color: connected ? 'success.main' : 'error.main' }}
          />
          <Typography variant="caption" color="text.secondary">
            {connected ? 'Connected' : 'Disconnected'}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Typography variant="caption" color="text.secondary">
          Capture: {capturing ? 'Active' : 'Stopped'}
        </Typography>
        <Divider orientation="vertical" flexItem />
        <Typography variant="caption" color="text.secondary">
          Mode: {extensionMode ? 'Extension (chrome.webRequest)' : connected ? 'WebSocket' : 'Demo'}
        </Typography>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={blockerEnabled ? `Blocker ON (${blockerRuleCount} rules)` : 'Blocker OFF'}
            size="small"
            color={blockerEnabled ? 'success' : 'default'}
            variant={blockerEnabled ? 'filled' : 'outlined'}
            icon={blockerEnabled ? <BlockIcon /> : undefined}
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
          <Button size="small" variant="outlined" onClick={handleToggleBlocker} disabled={!extensionMode} sx={{ fontSize: '0.65rem', minWidth: 'auto', px: 1, py: 0.25 }}>
            Toggle Blocker
          </Button>
          <Button size="small" variant="outlined" onClick={handleReloadFilters} disabled={!extensionMode} sx={{ fontSize: '0.65rem', minWidth: 'auto', px: 1, py: 0.25 }}>
            Reload Filters
          </Button>
        </Box>
        {!connected && (
          <>
            <Divider orientation="vertical" flexItem />
            <Typography variant="caption" color="warning.main">
              No extension detected - using demo data
            </Typography>
          </>
        )}
      </Paper>

      {/* Context Menu */}
      <MuiMenu
        open={contextMenu !== null}
        onClose={handleCloseContext}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleBlockUrl}>
          <BlockIcon fontSize="small" sx={{ mr: 1 }} /> Block this URL
        </MenuItem>
        <MenuItem onClick={handleBlockDomain}>
          <BlockIcon fontSize="small" sx={{ mr: 1 }} /> Block this domain
        </MenuItem>
        <MenuItem onClick={handleAllowUrl}>
          <AllowIcon fontSize="small" sx={{ mr: 1 }} /> Allow this URL
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleCreateRegexRule}>
          <CopyIcon fontSize="small" sx={{ mr: 1 }} /> Create regex rule
        </MenuItem>
      </MuiMenu>

      {/* Rule Dialog */}
      <RuleDialog
        open={ruleDialogOpen}
        onClose={() => {
          setRuleDialogOpen(false);
          setEditingRuleIndex(null);
        }}
        onSave={handleSaveRule}
        initialRule={editingRuleIndex !== null ? filters[editingRuleIndex] : null}
      />

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export as Network Rule Set</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Rule Set Name"
            fullWidth
            value={ruleSetName}
            onChange={(e) => setRuleSetName(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will create a new Network Rule Set with {filters.length} rule(s) and save it to the backend.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleExportRuleSet}
            disabled={!ruleSetName.trim()}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
